import ace from "ace-builds";
import { MessageType, MAPPABLE_FIELDS_CONFIG, SIGNABLE_DOCUMENTS_MAPPABLE_FIELDS_CONFIG } from "./constants";
import { Uuidv4 } from "../../../../../services/helpers";

let globalMappableFieldsCompleter = null;
let editorContexts = new Map(); // Map editor instances to their contexts

const getAllMappableFields = () => {
	const allFields = new Set();

	Object.values(MAPPABLE_FIELDS_CONFIG).forEach((config) => {
		Object.values(config).forEach((fields) => {
			fields.forEach((field) => allFields.add(field));
		});
	});

	return Array.from(allFields).sort();
};

const getMappableFields = (useCase, messageType = MessageType.email) => {
	const config = MAPPABLE_FIELDS_CONFIG[useCase];
	if (!config) {
		return [];
	}

	return config[messageType] || config[MessageType.email] || [];
};

export const addMappableFieldStyles = () => {
	const styleId = "mappable-fields-style";
	if (!document.getElementById(styleId)) {
		const style = document.createElement("style");
		style.id = styleId;
		style.textContent = `
      .mappable-field-marker {
        position: absolute;
        background-color: #53B3EA;
      }
    `;
		document.head.appendChild(style);
	}
};

const getSignableDocumentsMappableFields = (documentContext) => {
	const config = SIGNABLE_DOCUMENTS_MAPPABLE_FIELDS_CONFIG[documentContext];
	if (!config) {
		return [];
	}

	return config
}

export const setupMessageTemplatesMappableFieldsHighlighting = (
	editor,
	useCase = null,
	messageType
) => {
	setupMappableFieldsHighlighting(
		editor,
		{ useCase, messageType },
		(props) =>  props.useCase
			? getMappableFields(props.useCase, props.messageType)
			: getAllMappableFields()
	)
}

export const setupSignableDocumentsMappableFieldsHighlighting = (editor, documentContext) => 
	setupMappableFieldsHighlighting(
		editor,
		{ documentContext },
		(props) => getSignableDocumentsMappableFields(props.documentContext)
	);

const setupMappableFieldsHighlighting = (editor, props, mappableFieldsSource) => {
	if (!editor) return { dispose: () => {} };
	addMappableFieldStyles();

	const markers = [];
	const editorId = `editor_${Uuidv4()}`;

	editorContexts.set(editor, {
		editorId,
		props,
	});

	// Create global completer if it doesn't exist
	if (!globalMappableFieldsCompleter) {
		globalMappableFieldsCompleter = {
			id: "global-mappable-fields-completer",
			getCompletions: function (editor, session, pos, prefix, callback) {
				// Get context for the current editor
				const context = editorContexts.get(editor);
				if (!context) {
					callback(null, []);
					return;
				}

				// Check if we should show mappable fields
				const line = session.getLine(pos.row);
				const beforeCursor = line.substring(0, pos.column);

				// Look for '#' before the cursor
				const hashIndex = beforeCursor.lastIndexOf("#");
				if (hashIndex !== -1) {
					// Get current mappable fields dynamically using stored context
					const currentMappableFields = mappableFieldsSource(context.props);

					const completions = currentMappableFields.map((field) => ({
						caption: field,
						value: field,
						meta: null,
						score: 1000,
						// replace from the '#' position to cursor
						completer: {
							insertMatch: function (editor, data) {
								const pos = editor.getCursorPosition();
								const line = editor.session.getLine(pos.row);
								const beforeCursor = line.substring(0, pos.column);
								const hashIndex = beforeCursor.lastIndexOf("#");

								if (hashIndex !== -1) {
									// Replace from '#' to cursor with the complete field
									const range = ace
										.require("ace/range")
										.Range.fromPoints(
											{ row: pos.row, column: hashIndex },
											{ row: pos.row, column: pos.column }
										);
									editor.session.replace(range, data.value);
								}
							},
						},
					}));

					callback(null, completions);
				} else {
					callback(null, []);
				}
			},
		};

		const langTools = ace.require("ace/ext/language_tools");
		
		const completersToUse = [];
		
		if (langTools.keyWordCompleter) {
			completersToUse.push(langTools.keyWordCompleter);
		}
		
		if (langTools.snippetCompleter) {
			completersToUse.push(langTools.snippetCompleter);
		}
		
		// Add our custom completer
		completersToUse.push(globalMappableFieldsCompleter);
		
		// Set the completers (excluding textCompleter which provides local word suggestions)
		langTools.setCompleters(completersToUse);
	}

	// Enable autocomplete
	editor.setOptions({
		enableBasicAutocompletion: true,
		enableLiveAutocompletion: true,
		enableSnippets: false,
	});

	// Add command for this specific editor instance
	const commandName = `triggerFieldsAutocomplete_${editorId}`;
	editor.commands.addCommand({
		name: commandName,
		bindKey: "#",
		exec: function (editor) {
			editor.insert("#");
			editor.execCommand("startAutocomplete");
		},
	});

	// Function to update markers based on current editor content
	const updateMarkers = () => {
		// Clear previous markers
		markers.forEach((id) => {
			try {
				editor.session.removeMarker(id);
			} catch (e) {}
		});
		markers.length = 0;

		// Find all mappable fields with pattern: #text#
		const text = editor.getValue();
		const regex = /#([a-zA-Z0-9_]+)#/g;
		let match;

		const Range = ace.require("ace/range").Range;

		while ((match = regex.exec(text)) !== null) {
			const startIndex = match.index;
			const endIndex = startIndex + match[0].length;

			// Convert indices to row/column positions
			const startPos = editor.session.doc.indexToPosition(startIndex);
			const endPos = editor.session.doc.indexToPosition(endIndex);

			// Create a range for the marker
			const range = new Range(
				startPos.row,
				startPos.column,
				endPos.row,
				endPos.column
			);

			const id = editor.session.addMarker(
				range,
				"mappable-field-marker",
				"text",
				false
			);
			markers.push(id);
		}
	};

	editor.session.on("change", updateMarkers);

	updateMarkers();

	return {
		updateContext: (props) => {
			// Update context for this specific editor
			const context = editorContexts.get(editor);
			if (context) {
				context.props = props;
			}
			updateMarkers();
		},
		dispose: () => {
			if (editor && editor.session) {
				try {
					editor.session.off("change", updateMarkers);
					markers.forEach((id) => {
						try {
							editor.session.removeMarker(id);
						} catch (e) {}
					});
				} catch (e) {}
			}
			markers.length = 0;

			editorContexts.delete(editor);

			if (editor && editor.commands) {
				try {
					editor.commands.removeCommand(commandName);
				} catch (e) {}
			}

			if (globalMappableFieldsCompleter) {
				globalMappableFieldsCompleter = null;
				try {
					const langTools = ace.require("ace/ext/language_tools");
					if (langTools && editorContexts.size === 0) {
						langTools.setCompleters([
							langTools.keyWordCompleter,
							langTools.snippetCompleter,
						].filter(Boolean));
					}
				} catch (e) {}
			}
		},
	};
}

